import { UnitPresentation, UnitSystem } from 'MeasurementsCalculations';
import * as Data from 'Data';
import { observable, computed, autorun } from 'mobx';
import * as normalizationConfigs from 'Components/NormalizationSettings/configs';

export class GeneralReportStore {
  isTps = false;
  isWeb = false;
  isIos = false;
  instructorMessage = '';
  reportSelection = '';

  reportKind: Data.ReportKind = Data.ReportKind.notDefined;
  sponsors: Array<Data.ISponsorReportInfo> = [];
  facilities: Array<Data.IFacilityReportInfo> = [];
  //hiddenShots: Array<string> = null;

  @observable showBottomCommercial = true;
  @observable selectedGroupOfStrokes?: Data.IReportMeasurementGroup;
  @observable unitPresentation: UnitPresentation = new UnitPresentation(UnitSystem.Metric);
  @observable selectedUnit = 'm';
  @observable measurementsData: Array<Data.IReportMeasurementGroup> = [];
  @observable showReport = false;
  @observable selectedStroke?: Data.IReportMeasurement;
  @observable selectedAverage?: Data.IReportMeasurement;

  @observable editModeInTps = false;
  @computed get selectedStrokesGroups() {
    return this.measurementsData.filter(g => g.isSelected);
  }
  @observable deviceOnline = true;
  @observable selectedView: Data.ViewType = Data.ViewType.NotSpecified;

  @observable clubType = '';
  @observable instructorName = '';
  @observable instructorImage = '';
  @observable instructorId = '';
  @observable individualPlayers: Array<Data.IReportPerson> = [];
  @observable reportDate?: Date;
  @observable tilesToDisplay: Array<string> = [];
  @computed get videos() {
    return this.selectedStroke && !this.selectedAverage ? this.selectedStroke.videos : [];
  }
  @observable selectedVideos: Array<Data.IReportVideo> = [];
  @observable reportHasAtLeastOneVideo = false;
  // Normalization settings
  @observable showNormalizedData = false;
  @observable normalizationSettings?: normalizationConfigs.NormalizationSettingsType;
  @observable selectedOptimizerView: Data.OptimizerView = Data.OptimizerView.Data;
  //@observable hiddenStrokes: Array<string> = [];
  @computed get hiddenStrokes() {
    const result = new Array<any>();
    this.measurementsData.forEach(g =>
      g.strokes?.filter(x => !x.isVisible).forEach(stroke => result.push(stroke.id))
    );
    return result;
  }
  @observable measurementParameters: Array<Data.ISingleMeasurementParameter> = [];
  @observable reportId = '';
  @observable selectedDispersionMode: Data.DispersionMode = Data.DispersionMode.Carry;
  @observable summaryOn = false;
  @observable dispersionOn = false;
  @observable trajectoryOn = false;
  @observable optimizerOn = false;
  @observable skidAndRollOn = false;
  @observable videoOn = false;
  @observable clubDataOn = false;
  currentUrl = '';
  @observable reportPrintUrl = '';
  @observable shareLink = '';
  @observable showedPrintingDialog = false;
  @observable newestStrokeInfo?: Data.INewestStroke;
  @observable reportImage = '';
  @observable normalizedEnvironmentDetails?: Data.IReportEnvironment;
  @observable showOptimizerTutorial = false;
  @observable selectedOptimizedType: Data.IOptimizationType = Data.Carry;
  @observable noStrokes = false;
  siteName = '';
  siteShortName = '';

  //Combine and test center observables
  @observable testResult?: Data.ITestResult;

  constructor() {
    autorun(() => {
      console.log('Change of unit presentation', this.unitPresentation);
      this.unitPresentation.overrideConfiguration(this.reportKind);
    });
  }

  toReportConfiguration(): Data.IReportConfiguration {
    return {
      clubDataOn: this.clubDataOn,
      dispersionOn: this.dispersionOn,
      hiddenShots: this.hiddenStrokes,
      instructorMessage: this.instructorMessage,
      isIos: this.isIos,
      isTps: this.isTps,
      isWeb: this.isWeb,
      optimizerOn: this.optimizerOn,
      reportSelection: this.reportSelection,
      selectedClubType: this.clubType,
      selectedDispersionMode: Data.GetDispersionMode(this.selectedDispersionMode),
      selectedGroupOfStrokes: this.selectedGroupOfStrokes && this.selectedGroupOfStrokes?.id,
      selectedOptimizedType: this.selectedOptimizedType && this.selectedOptimizedType.value,
      selectedOptimizerView: this.selectedOptimizerView,
      selectedStroke: this.selectedStroke && this.selectedStroke.id,
      selectedStrokesGroups: this.selectedStrokesGroups.map(x => x.id),

      selectedVideoAngles: this.selectedVideos.map((video, index) => {
        let videoIndex = -1;
        if (this.measurementsData[index]) {
          if (
            this.reportKind === Data.ReportKind.compareReport ||
            this.reportKind === Data.ReportKind.puttingCompareReport
          ) {
            const strokes = this.measurementsData[index].strokes;
            if (strokes) {
              if (strokes[0]) {
                videoIndex = strokes[0].videos?.indexOf(video) ?? -1;
              }
            }
          } else {
            videoIndex = this.videos?.indexOf(video) ?? -1;
          }
        }
        if (videoIndex < 0) return 0;

        return videoIndex;
      }),
      selectedView: this.selectedView,
      showBottomCommercial: this.showBottomCommercial,
      showNormalizedData: this.showNormalizedData,
      /**
       * @description We should `stringify` normalizationSettings (with type of {@link normalizationConfigs.NormalizationSettingsType}) because it's an object.
       * Later on we will `parse` it. in {@link fromReportConfiguration} method
       */
      normalizationSettings: JSON.stringify(this.normalizationSettings),
      skidAndRollOn: this.skidAndRollOn,
      summaryOn: this.summaryOn,
      tiles: this.measurementParameters
        .filter(parameter => parameter.isVisible)
        .map(x => x.propertyName),
      trajectoryOn: this.trajectoryOn,
      units: this.selectedUnit,
      videoOn: this.videoOn,
    };
  }

  //@action
  fromReportConfiguration(reportConfiguration: Data.IReportConfiguration): GeneralReportStore {
    if (!this.measurementsData) {
      throw new ReferenceError('normalizedSessionMeasurements is null, converting imposible');
    }

    if (
      reportConfiguration.selectedOptimizedType &&
      !Data.GetOptimizationType(reportConfiguration.selectedOptimizedType)
    ) {
      console.log(
        new Error(
          `${reportConfiguration.selectedOptimizedType} can't be found in delcarated optimazed types`
        )
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const result = this;
    result.clubDataOn = reportConfiguration.clubDataOn;
    result.dispersionOn = reportConfiguration.dispersionOn;

    //result.hiddenShots = reportConfiguration.hiddenShots; // TODO: Should instead set isVisible on those particular strokes
    result.instructorMessage = reportConfiguration.instructorMessage;
    result.isIos = reportConfiguration.isIos;
    result.isTps = reportConfiguration.isTps;
    result.isWeb = reportConfiguration.isWeb;
    result.optimizerOn = reportConfiguration.optimizerOn;
    result.reportSelection = reportConfiguration.reportSelection;
    result.clubType = reportConfiguration.selectedClubType;
    result.selectedDispersionMode = Data.GetDispersionMode(
      reportConfiguration.selectedDispersionMode
    );

    for (let i = 0; i < this.measurementsData.length; i++) {
      if (
        reportConfiguration.selectedGroupOfStrokes &&
        this.measurementsData[i].id.toLowerCase() ===
          reportConfiguration.selectedGroupOfStrokes.toLowerCase()
      ) {
        result.selectedGroupOfStrokes = this.measurementsData[i];
      }

      if (reportConfiguration.selectedStroke) {
        const md = this.measurementsData[i];
        const s = md.strokes === undefined ? [] : md.strokes;
        for (let j = 0; j < s.length; j++) {
          const id = s[j].id ?? '';
          if (id.toLowerCase() === reportConfiguration.selectedStroke.toLowerCase()) {
            result.selectedStroke = s[j];
          }
        }
      }

      if (reportConfiguration.selectedStrokesGroups) {
        for (let k = 0; k < reportConfiguration.selectedStrokesGroups.length; k++) {
          if (
            this.measurementsData[i].id.toLowerCase() ===
            reportConfiguration.selectedStrokesGroups[k].toLowerCase()
          ) {
            result.measurementsData[i].isSelected = true;
            break;
          } else {
            result.measurementsData[i].isSelected = false;
          }
        }
      }

      if (reportConfiguration.tiles) {
        for (let j = 0; j < reportConfiguration.tiles.length; j++) {
          if (
            result.measurementParameters &&
            result.measurementParameters[i] &&
            result.measurementParameters[i].propertyName === reportConfiguration.tiles[j]
          ) {
            result.measurementParameters[i].isVisible = true;
            break;
          }
        }
      }
    }

    result.selectedOptimizedType = Data.GetDefaultOptimizedType(
      reportConfiguration.selectedOptimizedType
    );
    result.selectedOptimizerView = Data.GetOptimizerView(reportConfiguration.selectedOptimizerView);
    if (reportConfiguration.selectedVideoAngles) {
      result.selectedVideos = [];
      for (let i = 0; i < reportConfiguration.selectedVideoAngles.length; i++) {
        const index = reportConfiguration.selectedVideoAngles[i];
        const video = result.videos && result.videos[index];
        if (video) {
          if (result.selectedVideos.length - 1 < i) {
            result.selectedVideos.push({} as Data.IReportVideo);
          }
          result.selectedVideos[i] = video;
        }
      }
    }

    this.measurementsData.forEach(g => {
      g.strokes = reportConfiguration.showNormalizedData ? g.normalStrokes : g.normalizedStrokes;
      g.strokes.forEach(s => {
        if (result.selectedStroke && s.id === result.selectedStroke.id) {
          result.selectedStroke = s;
        }

        if (reportConfiguration.hiddenShots) {
          const isShotHidden = reportConfiguration.hiddenShots.filter(x => x === s.id);
          s.isVisible = !isShotHidden.length;
        }
      });
    });

    result.selectedView = reportConfiguration.selectedView;
    result.showBottomCommercial = reportConfiguration.showBottomCommercial ?? false;
    result.showNormalizedData = reportConfiguration.showNormalizedData;
    result.normalizationSettings = JSON.parse(reportConfiguration.normalizationSettings);
    result.skidAndRollOn = reportConfiguration.skidAndRollOn;
    result.summaryOn = reportConfiguration.summaryOn ?? false;

    result.trajectoryOn = reportConfiguration.trajectoryOn;
    result.selectedUnit = reportConfiguration.units;
    result.unitPresentation = new UnitPresentation(Data.GetSymbolUnitSystem(result.selectedUnit));
    result.videoOn = reportConfiguration.videoOn;
    return result;
  }
}
